import { Controller } from '@hotwired/stimulus'
import SFetch from '@app/utils/sfetch'
import type { SfJQuery } from '@app/types'

export default class extends Controller {
  static targets = [
    'modalContainer',
    'confirmInput',
    'confirmButton',
    'errorMessage',
  ]
  static values = {
    firmId: Number,
    syncEntityId: Number,
    companiesCount: Number,
    syncType: String,
    currentlyDisconnecting: Boolean,
  }

  declare readonly modalContainerTarget: HTMLElement
  declare readonly confirmInputTarget: HTMLInputElement
  declare readonly confirmButtonTarget: HTMLButtonElement
  declare readonly errorMessageTarget: HTMLElement
  declare readonly hasErrorMessageTarget: boolean
  declare readonly firmIdValue: number
  declare readonly syncEntityIdValue: number
  declare readonly companiesCountValue: number
  declare readonly syncTypeValue: string
  declare readonly currentlyDisconnectingValue: boolean

  connect(): void {
    this.modalContainerTarget.addEventListener('open-modal', () => {
      this.openModal()
    })
  }

  openModal(event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    if (this.currentlyDisconnectingValue) {
      return
    }

    this.modalContainerTarget.style.display = 'block'

    if (typeof $ !== 'undefined') {
      const modalRoot = this.modalContainerTarget
        .firstElementChild as HTMLElement
      if (modalRoot) {
        const $modal = $(modalRoot) as SfJQuery
        if ($modal.sfModal) {
          $modal.sfModal()
        }
      }
    }

    this.confirmInputTarget.value = ''
    this.setConfirmButtonDisabled(true)
    this.hideError()
  }

  closeModal(event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    this.modalContainerTarget.style.display = 'none'
    this.resetForm()
  }

  private resetForm(): void {
    this.modalContainerTarget.style.display = 'none'
    this.confirmInputTarget.value = ''
    this.setConfirmButtonDisabled(true)
  }

  validateInput(): void {
    const expectedText = this.getExpectedConfirmationText()
    const inputValue = this.confirmInputTarget.value.trim()
    const matches = inputValue.toLowerCase() === expectedText.toLowerCase()
    this.setConfirmButtonDisabled(!matches)
  }

  async confirmDisconnect(event: Event): Promise<void> {
    event.preventDefault()

    if (this.confirmButtonTarget.disabled) {
      return
    }

    this.setConfirmButtonDisabled(true)
    this.setConfirmButtonLabel(
      window.I18n.t('sync_entities.disconnect_modal.disconnecting')
    )

    try {
      const routes = window.Silverfin.Routes
      const isAdminSync = this.syncTypeValue === 'admin'

      const url = isAdminSync
        ? routes.disconnect_all_companies_administration_sync_entity_path(
            String(this.firmIdValue),
            String(this.syncEntityIdValue)
          )
        : routes.disconnect_all_companies_sync_entity_path(
            String(this.firmIdValue),
            String(this.syncEntityIdValue)
          )

      const data = await SFetch.json<{ success: boolean; error?: string }>(
        url,
        {
          method: 'POST',
        }
      )

      if (data?.success) {
        const syncEntitiesUrl = isAdminSync
          ? routes.administration_sync_entities_path(String(this.firmIdValue))
          : routes.sync_entities_path(String(this.firmIdValue))
        window.location.href = syncEntitiesUrl
      } else {
        this.showError(window.I18n.t('sync_entities.flashes.disconnect_error'))
        this.resetConfirmButton()
      }
    } catch {
      this.showError(window.I18n.t('sync_entities.flashes.disconnect_error'))
      this.resetConfirmButton()
    }
  }

  private setConfirmButtonDisabled(disabled: boolean): void {
    this.confirmButtonTarget.disabled = disabled
  }

  private getExpectedConfirmationText(): string {
    return `disconnect-all-${this.companiesCountValue}`
  }

  private showError(message: string): void {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.style.display = 'block'
    }
  }

  private hideError(): void {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ''
      this.errorMessageTarget.style.display = 'none'
    }
  }

  private resetConfirmButton(): void {
    this.setConfirmButtonLabel(
      window.I18n.t('sync_entities.disconnect_modal.confirm_button')
    )
    this.setConfirmButtonDisabled(false)
  }

  private setConfirmButtonLabel(text: string): void {
    const label = this.confirmButtonTarget.querySelector('.sf-button__label')
    if (label) label.textContent = text
  }
}
